import {useEffect, useMemo, useState} from 'react';
import {
  AIMode,
  AssetType,
  AssistantType,
  Common,
  CurrencyAdapter,
  NumBool,
} from '@taopix/taopix-core';
import {useSelector} from 'react-redux';
import {LayoutSelector, TotalPageCountSelector} from '../Selectors';
import {useTaopix} from '../Components/Context/TaopixContext';
import {AssetMetaInterface, ExperienceType} from '@taopix/taopix-core/dist/types/types';
import {useUiState} from '../Components/Context/UserInterface/UserInterfaceContext';
import {UserInterfaceActions} from '../Components/Context/UserInterface/UserInterfaceActionTypes';
import useSettings from './useSettings';
import {ROUTE_ASSISTANT} from '../Constants';
import {useUserFlowState} from '../Components/Context/UserFlow/UserFlowContext';

export const usePageCountUpsellObserver = (experience: ExperienceType) => {
  const app = useTaopix();
  const ui = useUiState();
  const settings = useSettings();
  const productLayout = useSelector(LayoutSelector);
  const pageCount = useSelector(TotalPageCountSelector);
  // If page upsell is off, default to 1 per page.
  const recommendedPicturesPerPage = experience?.EDITOR?.upsellPages?.enableUpsellPages
    ? experience?.EDITOR?.upsellPages?.recommendedAveragePicturesPerPage
    : 1;

  const [count, setCount] = useState<number>(0);

  const productQuantity = productLayout?.pricing?.quantity || 0;
  const currency = useMemo(
    () => CurrencyAdapter(settings.state.currency),
    [settings.state.currency.code]
  );
  let pricingEngine;

  if (productLayout && productLayout?.pricing) {
    pricingEngine = new (app.core.get('pricingEngine'))(
      currency,
      productQuantity,
      productLayout?.pricing,
      AIMode.Off
    );
  }

  const assets = useSelector<any>(state =>
    Object.values<AssetMetaInterface>(state.project.assets).filter(
      a => a.assetType === AssetType.User
    )
  ) as AssetMetaInterface[];

  useEffect(() => {
    if (!app.state.loaded) return;

    ui.dispatch(
      UserInterfaceActions.setPicturePaletteSortOrder({
        sortOrder: experience.EDITOR.pictures.defaultPictureManagerSortOrder,
        sortDirection: 'asc',
      })
    );
  }, [app.state.loaded]);

  useEffect(() => {
    if (!productLayout) return;
    if (document.location.hash.includes(`#/${ROUTE_ASSISTANT}`)) return;

    // base price of current project
    const basePrice = pricingEngine?.calculateProjectPrice(pageCount, [], 0, 0);

    // get the recommended page count based on num of assets, pictures per page and max pages
    const recommendedPageCount = (() => {
      const pageInsertCount = productLayout.pageinsertcount;
      let recommended = 0;

      if (experience?.EDITOR?.upsellPages?.enableUpsellPages) {
        let assetLength = assets.length;

        // If we have covers, we take them off the assets length in order to calculate the recommended.
        if (productLayout.cover.cover1active) {
          assetLength -= 1;
        }

        if (productLayout.cover.cover2active) {
          assetLength -= 1;
        }

        recommended = Math.min(
          Math.ceil(assetLength / recommendedPicturesPerPage),
          productLayout.maxpages
        );

        // Make sure that the recommended number of pages is a viable option for the layout.
        if (recommended > productLayout.maxpages) {
          recommended = productLayout.maxpages - pageInsertCount;
        }

        if (recommended !== 0 && recommended <= productLayout.pagecount) {
          recommended = productLayout.pagecount;
        }
      } else {
        recommended = Math.min(
          Math.max(assets.length, productLayout.pagecount),
          productLayout.maxpages
        );
      }

      recommended = Math.max(productLayout.minpages, recommended);

      // if (0 !== recommended % pageInsertCount) {
      //   // Recommended is not a multiple of the page insert count so find the closest value.
      //   recommended = Common.roundToNextMultiple(recommended, pageInsertCount);
      // }

      // If the recommendedPageCount is less or equal to current page count then just set the recommended to the current.
      if (recommended !== 0 && recommended <= pageCount) {
        recommended = Common.roundToNextMultiple(pageCount, pageInsertCount);
      }

      /*
      If we have a spread with but the recommended difference is odd add 1.
      This is because are now not taking into account the pageintertcount when calculating the recommened and
      we cannot insert an odd number of pages for a spread.
      */
      if (productLayout.spreads === NumBool.True && (recommended - pageCount) % 2 === 1) {
        recommended += 1;
      }

      return recommended;
    })();

    let recommendedPageCountPricing = '';
    if (0 !== recommendedPageCount) {
      // new price of project using the recommendedPageCount calculated above
      const recommendedNewPrice = pricingEngine?.calculateProjectPrice(
        recommendedPageCount,
        [],
        0,
        0
      );
      const rawPriceDiff = recommendedNewPrice - basePrice;
      const priceDiff = app.core.get('pricingEngine').formatCurrencyNumber(rawPriceDiff, currency);

      // price of new pages based on fitting the pics per page setting
      recommendedPageCountPricing = `${rawPriceDiff >= 0 ? '+' : ''}${priceDiff}`;
    }

    // If the max and recommended page count is different, calculate the pages count and the price, otherwise use recommended.
    let maxPageCount: number;
    if (productLayout.maxpages !== recommendedPageCount) {
      maxPageCount = Math.min(assets.length, productLayout.maxpages);
    } else {
      maxPageCount = recommendedPageCount;
    }

    // Make sure that the 1 picture per page number of pages is a viable option for the layout.
    if (0 !== maxPageCount % productLayout.pageinsertcount) {
      maxPageCount =
        maxPageCount -
        (maxPageCount % productLayout.pageinsertcount) +
        productLayout.pageinsertcount;
    }

    // Calculate the price for the 1 picture per page / max pages option.
    const maxPageCountPrice = pricingEngine?.calculateProjectPrice(maxPageCount, [], 0, 0);
    const rawPriceDiffMax = maxPageCountPrice - basePrice;
    const priceDiffMax = app.core
      .get('pricingEngine')
      .formatCurrencyNumber(rawPriceDiffMax, currency);

    // price of new pages based on 1 picture per page
    const maxPageCountPricing = `${rawPriceDiffMax > 0 ? '+' : ''}${priceDiffMax}`;

    if (
      experience?.EDITOR?.upsellPages?.enableUpsellPages &&
      experience?.WIZARD?.designAssistantType?.designAssistantType !== AssistantType.SmartDesign
    ) {
      // only set this flag the first time the threshold is hit as it triggers the addPages button popout changes which we only want to happen first time
      if (
        pageCount !== productLayout.maxpages &&
        assets.length > pageCount * recommendedPicturesPerPage &&
        recommendedPageCount - pageCount > 0 &&
        count === 0
      ) {
        setCount(count + 1);
        ui.dispatch(
          UserInterfaceActions.setFlags({
            recommendAddingPictures: true,
          })
        );
      } else {
        ui.dispatch(
          UserInterfaceActions.setFlags({
            recommendAddingPictures: false,
          })
        );
      }
    }

    ui.dispatch(
      UserInterfaceActions.setAutoFillOptions({
        recommendedPageCount: recommendedPageCount,
        recommendedPageCountPrice: recommendedPageCountPricing,
        maxPageCount: maxPageCount,
        maxPageCountPrice: maxPageCountPricing, // not needed?
      })
    );
  }, [assets.length, pageCount]);
};
